/**
 * Settings Page
 *
 * Three sections:
 *  1. Document Templates — uploaded files (R2), name/description editable, file replaceable
 *  2. Cost Models         — toggle active/inactive and edit labels
 *  3. Mail                — operational links to Make.com scenarios
 *  4. Admin Users          — list, add, edit, change password, delete
 *
 * Follows the spec in project-brain/pages/settings.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Card from '@components/ui/Card';
import Badge from '@components/ui/Badge';
import Button from '@components/ui/Button';
import Input from '@components/ui/Input';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import ErrorState from '@components/ui/ErrorState';
import './Settings.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DocumentTemplate {
  id: string;
  name: string;
  description: string | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface CostModel {
  id: string;
  label: string;
  sort_order: number;
  is_active: number;
  created_at: string;
  updated_at: string;
}

interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  is_active: number;
  created_at: string;
  updated_at: string;
}

interface EmailAutomation {
  key: string;
  name: string;
  make_scenario_url: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface SettingsData {
  document_templates: DocumentTemplate[];
  cost_models: CostModel[];
  email_automations: EmailAutomation[];
}

interface GitHubAppRepo {
  installation_id: number;
  owner: string;
  repo: string;
  full_name: string;
}

interface NewsletterSubscribersData {
  count: number;
  emails: string[];
}

type UserModalMode = 'add' | 'edit' | 'password' | 'delete' | null;
type TemplateModalMode = 'edit' | null;
type CostModelModalMode = 'edit' | null;
type EmailAutomationModalMode = 'edit' | null;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

const Settings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // ── GitHub Repos ───────────────────────────────────────────────────────────
  const [githubRepos, setGithubRepos] = useState<GitHubAppRepo[]>([]);
  const [githubReposLoading, setGithubReposLoading] = useState(true);
  const [githubReposError, setGithubReposError] = useState<string | null>(null);
  const [installUrlLoading, setInstallUrlLoading] = useState(false);
  const [githubAppToast, setGithubAppToast] = useState<string | null>(null);

  // ── Settings data ──────────────────────────────────────────────────────────
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [costModels, setCostModels] = useState<CostModel[]>([]);
  const [emailAutomations, setEmailAutomations] = useState<EmailAutomation[]>([]);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState<string | null>(null);

  // ── Newsletter subscribers state ───────────────────────────────────────────
  const [newsletterLoading, setNewsletterLoading] = useState(true);
  const [newsletterError, setNewsletterError] = useState<string | null>(null);
  const [newsletterCount, setNewsletterCount] = useState(0);
  const [newsletterEmails, setNewsletterEmails] = useState<string[]>([]);
  const [newsletterCopySuccess, setNewsletterCopySuccess] = useState(false);

  // ── Admin users ────────────────────────────────────────────────────────────
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);

  // ── User modal state ───────────────────────────────────────────────────────
  const [userModalMode, setUserModalMode] = useState<UserModalMode>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // User form
  const [formEmail, setFormEmail] = useState('');
  const [formFullName, setFormFullName] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // ── Template modal state ───────────────────────────────────────────────────
  const [templateModalMode, setTemplateModalMode] = useState<TemplateModalMode>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | null>(null);
  const [templateModalError, setTemplateModalError] = useState<string | null>(null);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [formTemplateName, setFormTemplateName] = useState('');
  const [formTemplateDesc, setFormTemplateDesc] = useState('');

  // ── Template file upload state ─────────────────────────────────────────────
  const [templateUploadingId, setTemplateUploadingId] = useState<string | null>(null);
  const [templateUploadError, setTemplateUploadError] = useState<string | null>(null);

  // ── Cost model modal state ─────────────────────────────────────────────────
  const [costModelModalMode, setCostModelModalMode] = useState<CostModelModalMode>(null);
  const [selectedCostModel, setSelectedCostModel] = useState<CostModel | null>(null);
  const [costModelModalError, setCostModelModalError] = useState<string | null>(null);
  const [costModelSaving, setCostModelSaving] = useState(false);
  const [formCostModelLabel, setFormCostModelLabel] = useState('');
  const [formCostModelActive, setFormCostModelActive] = useState(true);

  // ── Email automations modal state ───────────────────────────────────────────
  const [emailAutomationModalMode, setEmailAutomationModalMode] = useState<EmailAutomationModalMode>(null);
  const [selectedEmailAutomation, setSelectedEmailAutomation] = useState<EmailAutomation | null>(null);
  const [emailAutomationModalError, setEmailAutomationModalError] = useState<string | null>(null);
  const [emailAutomationSaving, setEmailAutomationSaving] = useState(false);
  const [formEmailAutomationName, setFormEmailAutomationName] = useState('');
  const [formEmailAutomationScenarioUrl, setFormEmailAutomationScenarioUrl] = useState('');

  // ─── Fetch settings ────────────────────────────────────────────────────────

  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const res = await fetch('/api/settings');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json() as {
        success: boolean;
        data: SettingsData;
      };
      if (!json.success) throw new Error('API returned failure');
      setTemplates(json.data.document_templates);
      setCostModels(json.data.cost_models);
      setEmailAutomations(json.data.email_automations ?? []);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      setSettingsError('Failed to load settings. Please try again.');
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  // ─── Email automation modal ────────────────────────────────────────────────

  const openEditEmailAutomationModal = (automation: EmailAutomation) => {
    setFormEmailAutomationName(automation.name);
    setFormEmailAutomationScenarioUrl(automation.make_scenario_url);
    setEmailAutomationModalError(null);
    setSelectedEmailAutomation(automation);
    setEmailAutomationModalMode('edit');
  };

  const closeEmailAutomationModal = () => {
    setEmailAutomationModalMode(null);
    setSelectedEmailAutomation(null);
    setEmailAutomationModalError(null);
  };

  const handleSaveEmailAutomation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmailAutomation) return;

    if (!formEmailAutomationName.trim()) {
      setEmailAutomationModalError('Name is required.');
      return;
    }

    setEmailAutomationSaving(true);
    setEmailAutomationModalError(null);
    try {
      const res = await fetch('/api/settings/email-automations', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: selectedEmailAutomation.key,
          name: formEmailAutomationName.trim(),
          make_scenario_url: formEmailAutomationScenarioUrl.trim(),
          sort_order: selectedEmailAutomation.sort_order,
        }),
      });
      const json = await res.json() as { success: boolean; message?: string };
      if (!res.ok || !json.success) {
        throw new Error(json.message ?? `HTTP ${res.status}`);
      }
      await fetchSettings();
      closeEmailAutomationModal();
    } catch (err: unknown) {
      setEmailAutomationModalError(err instanceof Error ? err.message : 'Failed to save email automation.');
    } finally {
      setEmailAutomationSaving(false);
    }
  };

  // ─── Fetch users ───────────────────────────────────────────────────────────

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const res = await fetch('/api/settings/users');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json() as { success: boolean; data: AdminUser[] };
      if (!json.success) throw new Error('API returned failure');
      setUsers(json.data);
    } catch (err) {
      console.error('Failed to fetch admin users:', err);
      setUsersError('Failed to load admin users. Please try again.');
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const fetchNewsletterSubscribers = useCallback(async () => {
    setNewsletterLoading(true);
    setNewsletterError(null);
    try {
      const res = await fetch('/api/settings/newsletter-subscribers');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as NewsletterSubscribersData;
      setNewsletterCount(json.count ?? 0);
      setNewsletterEmails(Array.isArray(json.emails) ? json.emails : []);
    } catch (err) {
      console.error('Failed to fetch newsletter subscribers:', err);
      setNewsletterError('Failed to load newsletter subscribers. Please try again.');
    } finally {
      setNewsletterLoading(false);
    }
  }, []);

  const handleCopyNewsletterEmails = async () => {
    try {
      const text = newsletterEmails.join(', ');
      await navigator.clipboard.writeText(text);
      setNewsletterCopySuccess(true);
      setTimeout(() => setNewsletterCopySuccess(false), 2000);
    } catch {
      setNewsletterError('Copy failed. Please copy the list manually.');
    }
  };

  const fetchGithubRepos = useCallback(async () => {
    setGithubReposLoading(true);
    setGithubReposError(null);
    try {
      const res = await fetch('/api/github-app/installations');
      const json = await res.json() as { success: boolean; data: GitHubAppRepo[]; message?: string };
      if (!json.success) throw new Error(json.message ?? 'Failed to load GitHub repos');
      setGithubRepos(json.data);
    } catch (err: unknown) {
      setGithubReposError(err instanceof Error ? err.message : 'Failed to load GitHub repos.');
    } finally {
      setGithubReposLoading(false);
    }
  }, []);

  const handleGrantGithubAccess = async () => {
    setInstallUrlLoading(true);
    setGithubReposError(null);
    try {
      const res = await fetch('/api/github-app/install-url');
      const json = await res.json() as { success: boolean; data: { install_url: string }; message?: string };
      if (!json.success) throw new Error(json.message ?? 'Failed to start the GitHub install flow');
      window.location.href = json.data.install_url;
    } catch (err: unknown) {
      setGithubReposError(err instanceof Error ? err.message : 'Failed to start the GitHub install flow.');
      setInstallUrlLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchUsers();
    fetchNewsletterSubscribers();
    fetchGithubRepos();
  }, [fetchSettings, fetchUsers, fetchNewsletterSubscribers, fetchGithubRepos]);

  // Handle the redirect flag from the GitHub App install callback
  // (project-brain/api/github-app.md) -- show a toast, then clear it from the URL.
  useEffect(() => {
    const flag = searchParams.get('github_app');
    if (!flag) return;
    setGithubAppToast(
      flag === 'connected'
        ? 'GitHub App access updated.'
        : 'Something went wrong granting GitHub access -- please try again.'
    );
    fetchGithubRepos();
    const next = new URLSearchParams(searchParams);
    next.delete('github_app');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Document template modal ───────────────────────────────────────────────

  const openEditTemplateModal = (template: DocumentTemplate) => {
    setFormTemplateName(template.name);
    setFormTemplateDesc(template.description ?? '');
    setTemplateModalError(null);
    setSelectedTemplate(template);
    setTemplateModalMode('edit');
  };

  const closeTemplateModal = () => {
    setTemplateModalMode(null);
    setSelectedTemplate(null);
    setTemplateModalError(null);
  };

  const handleSaveTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTemplate) return;
    if (!formTemplateName.trim()) {
      setTemplateModalError('Name is required.');
      return;
    }

    setTemplateSaving(true);
    setTemplateModalError(null);
    try {
      const res = await fetch('/api/settings/document-templates', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: selectedTemplate.id,
          name: formTemplateName.trim(),
          description: formTemplateDesc.trim(),
        }),
      });
      const json = await res.json() as { success: boolean; message?: string };
      if (!res.ok || !json.success) {
        throw new Error(json.message ?? `HTTP ${res.status}`);
      }
      await fetchSettings();
      closeTemplateModal();
    } catch (err: unknown) {
      setTemplateModalError(err instanceof Error ? err.message : 'Failed to save template.');
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleUploadTemplateFile = async (templateId: string, file: File) => {
    setTemplateUploadingId(templateId);
    setTemplateUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`/api/settings/document-templates/${templateId}/upload`, {
        method: 'POST',
        body: formData,
      });
      const json = await res.json() as { success: boolean; message?: string };
      if (!res.ok || !json.success) {
        throw new Error(json.message ?? `HTTP ${res.status}`);
      }
      await fetchSettings();
    } catch (err: unknown) {
      setTemplateUploadError(err instanceof Error ? err.message : 'Failed to upload template file.');
    } finally {
      setTemplateUploadingId(null);
    }
  };

  // ─── Cost model modal ──────────────────────────────────────────────────────

  const openEditCostModelModal = (costModel: CostModel) => {
    setFormCostModelLabel(costModel.label);
    setFormCostModelActive(costModel.is_active === 1);
    setCostModelModalError(null);
    setSelectedCostModel(costModel);
    setCostModelModalMode('edit');
  };

  const closeCostModelModal = () => {
    setCostModelModalMode(null);
    setSelectedCostModel(null);
    setCostModelModalError(null);
  };

  const handleSaveCostModel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCostModel) return;
    if (!formCostModelLabel.trim()) {
      setCostModelModalError('Label is required.');
      return;
    }

    setCostModelSaving(true);
    setCostModelModalError(null);
    try {
      const res = await fetch('/api/settings/cost-models', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: selectedCostModel.id,
          label: formCostModelLabel.trim(),
          is_active: formCostModelActive,
        }),
      });
      const json = await res.json() as { success: boolean; message?: string };
      if (!res.ok || !json.success) {
        throw new Error(json.message ?? `HTTP ${res.status}`);
      }
      await fetchSettings();
      closeCostModelModal();
    } catch (err: unknown) {
      setCostModelModalError(err instanceof Error ? err.message : 'Failed to save cost model.');
    } finally {
      setCostModelSaving(false);
    }
  };

  // ─── User modal helpers ────────────────────────────────────────────────────

  const openAddModal = () => {
    setFormEmail('');
    setFormFullName('');
    setFormPassword('');
    setFormIsActive(true);
    setModalError(null);
    setSelectedUser(null);
    setUserModalMode('add');
  };

  const openEditModal = (user: AdminUser) => {
    setFormEmail(user.email);
    setFormFullName(user.full_name);
    setFormIsActive(user.is_active === 1);
    setModalError(null);
    setSelectedUser(user);
    setUserModalMode('edit');
  };

  const openPasswordModal = (user: AdminUser) => {
    setNewPassword('');
    setConfirmPassword('');
    setModalError(null);
    setSelectedUser(user);
    setUserModalMode('password');
  };

  const openDeleteModal = (user: AdminUser) => {
    setModalError(null);
    setSelectedUser(user);
    setUserModalMode('delete');
  };

  const closeUserModal = () => {
    setUserModalMode(null);
    setSelectedUser(null);
    setModalError(null);
  };

  // ─── Add user ──────────────────────────────────────────────────────────────

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formEmail.trim() || !formFullName.trim() || !formPassword) {
      setModalError('Email, full name, and password are required.');
      return;
    }
    if (formPassword.length < 8) {
      setModalError('Password must be at least 8 characters.');
      return;
    }

    setSaving(true);
    setModalError(null);
    try {
      const res = await fetch('/api/settings/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formEmail.trim(),
          full_name: formFullName.trim(),
          password: formPassword,
        }),
      });
      const json = await res.json() as { success: boolean; message?: string };
      if (!res.ok || !json.success) {
        throw new Error(json.message ?? `HTTP ${res.status}`);
      }
      await fetchUsers();
      closeUserModal();
    } catch (err: unknown) {
      setModalError(err instanceof Error ? err.message : 'Failed to create user.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Edit user ─────────────────────────────────────────────────────────────

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    if (!formEmail.trim() || !formFullName.trim()) {
      setModalError('Email and full name are required.');
      return;
    }

    setSaving(true);
    setModalError(null);
    try {
      const res = await fetch(`/api/settings/users/${selectedUser.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formEmail.trim(),
          full_name: formFullName.trim(),
          is_active: formIsActive,
        }),
      });
      const json = await res.json() as { success: boolean; message?: string };
      if (!res.ok || !json.success) {
        throw new Error(json.message ?? `HTTP ${res.status}`);
      }
      await fetchUsers();
      closeUserModal();
    } catch (err: unknown) {
      setModalError(err instanceof Error ? err.message : 'Failed to update user.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Change password ───────────────────────────────────────────────────────

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    if (!newPassword) {
      setModalError('New password is required.');
      return;
    }
    if (newPassword.length < 8) {
      setModalError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setModalError('Passwords do not match.');
      return;
    }

    setSaving(true);
    setModalError(null);
    try {
      const res = await fetch(`/api/settings/users/${selectedUser.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPassword }),
      });
      const json = await res.json() as { success: boolean; message?: string };
      if (!res.ok || !json.success) {
        throw new Error(json.message ?? `HTTP ${res.status}`);
      }
      closeUserModal();
    } catch (err: unknown) {
      setModalError(err instanceof Error ? err.message : 'Failed to change password.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete user ───────────────────────────────────────────────────────────

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setSaving(true);
    setModalError(null);
    try {
      const res = await fetch(`/api/settings/users/${selectedUser.id}`, {
        method: 'DELETE',
      });
      const json = await res.json() as { success: boolean; message?: string };
      if (!res.ok || !json.success) {
        throw new Error(json.message ?? `HTTP ${res.status}`);
      }
      await fetchUsers();
      closeUserModal();
    } catch (err: unknown) {
      setModalError(err instanceof Error ? err.message : 'Failed to delete user.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="settings-page">
      {/* Page header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Settings</h1>
        </div>
      </div>

      {/* ── Document Templates ─────────────────────────────────────────────── */}
      <section className="settings-section">
        <div className="settings-section-header">
          <div>
            <h2 className="settings-section-title">Document Templates</h2>
            <p className="settings-section-desc">Standard document template files used for engagements.</p>
          </div>
        </div>

        {settingsLoading ? (
          <Card>
            <div className="settings-loading">
              <LoadingSpinner size="md" />
            </div>
          </Card>
        ) : settingsError ? (
          <Card>
            <ErrorState message={settingsError} onRetry={fetchSettings} />
          </Card>
        ) : (
          <Card>
            {templates.length === 0 ? (
              <p className="settings-empty">No document templates found.</p>
            ) : (
              <div className="settings-list">
                {templateUploadError && <p className="modal-error">{templateUploadError}</p>}
                {templates.map((template) => (
                  <div key={template.id} className="settings-list-item">
                    <div className="settings-list-item-body">
                      <div className="settings-list-item-name">{template.name}</div>
                      {template.description && (
                        <div className="settings-list-item-desc">{template.description}</div>
                      )}
                      {template.file_size_bytes != null ? (
                        <a
                          href={`/api/settings/document-templates/${template.id}/download`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="settings-list-item-link"
                        >
                          Open file
                        </a>
                      ) : (
                        <span className="settings-list-item-empty-link">No file uploaded</span>
                      )}
                    </div>
                    <div className="settings-list-item-actions">
                      <input
                        type="file"
                        id={`template-upload-${template.id}`}
                        style={{ display: 'none' }}
                        disabled={templateUploadingId === template.id}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (file) handleUploadTemplateFile(template.id, file);
                        }}
                      />
                      <label
                        htmlFor={`template-upload-${template.id}`}
                        className="users-action-btn"
                        style={{ cursor: templateUploadingId === template.id ? 'not-allowed' : 'pointer' }}
                      >
                        {templateUploadingId === template.id
                          ? 'Uploading…'
                          : template.file_size_bytes != null
                          ? 'Replace file'
                          : 'Upload file'}
                      </label>
                      <button
                        className="users-action-btn"
                        type="button"
                        onClick={() => openEditTemplateModal(template)}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </section>

      {/* ── Cost Models ────────────────────────────────────────────────────── */}
      <section className="settings-section">
        <div className="settings-section-header">
          <div>
            <h2 className="settings-section-title">Cost Models</h2>
            <p className="settings-section-desc">Available cost model options used when creating projects.</p>
          </div>
        </div>

        {settingsLoading ? (
          <Card>
            <div className="settings-loading">
              <LoadingSpinner size="md" />
            </div>
          </Card>
        ) : settingsError ? null : (
          <Card>
            {costModels.length === 0 ? (
              <p className="settings-empty">No cost models found.</p>
            ) : (
              <div className="settings-list">
                {costModels.map((cm) => (
                  <div key={cm.id} className="settings-list-item">
                    <div className="settings-list-item-body">
                      <div className="settings-list-item-name">{cm.label}</div>
                    </div>
                    <div className="settings-list-item-actions">
                      <Badge variant={cm.is_active ? 'success' : 'default'}>
                        {cm.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      <button
                        className="users-action-btn"
                        type="button"
                        onClick={() => openEditCostModelModal(cm)}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </section>

      {/* ── Mail ────────────────────────────────────────────────────────────── */}
      <section className="settings-section">
        <div className="settings-section-header">
          <div>
            <h2 className="settings-section-title">Mail</h2>
            <p className="settings-section-desc">Operational links to Make.com scenarios that own outbound email sending.</p>
          </div>
        </div>

        {settingsLoading ? (
          <Card>
            <div className="settings-loading">
              <LoadingSpinner size="md" />
            </div>
          </Card>
        ) : settingsError ? null : (
          <Card>
            {emailAutomations.length === 0 ? (
              <p className="settings-empty">No mail automations configured.</p>
            ) : (
              <div className="settings-list">
                {emailAutomations.map((a) => (
                  <div key={a.key} className="settings-list-item">
                    <div className="settings-list-item-body">
                      <div className="settings-list-item-name">{a.name}</div>
                      {a.make_scenario_url ? (
                        <a
                          href={a.make_scenario_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="settings-list-item-link"
                        >
                          {a.make_scenario_url}
                        </a>
                      ) : (
                        <span className="settings-list-item-empty-link">No scenario URL set</span>
                      )}
                    </div>
                    <div className="settings-list-item-actions">
                      <button
                        className="users-action-btn"
                        type="button"
                        onClick={() => openEditEmailAutomationModal(a)}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </section>

      {/* ── Admin Users ────────────────────────────────────────────────────── */}
      <section className="settings-section">
        <div className="settings-section-header">
          <div>
            <h2 className="settings-section-title">Newsletter Subscribers</h2>
            <p className="settings-section-desc">Copy subscriber emails for newsletter BCC.</p>
          </div>
        </div>

        {newsletterLoading ? (
          <Card>
            <div className="settings-loading">
              <LoadingSpinner size="md" />
            </div>
          </Card>
        ) : newsletterError ? (
          <Card>
            <ErrorState message={newsletterError} onRetry={fetchNewsletterSubscribers} />
          </Card>
        ) : (
          <Card>
            <div className="newsletter-subscribers-card">
              <p className="newsletter-subscribers-count">Subscribers: {newsletterCount}</p>

              {newsletterEmails.length === 0 ? (
                <p className="settings-empty">No newsletter subscribers yet.</p>
              ) : (
                <>
                  <textarea
                    className="newsletter-subscribers-textarea"
                    readOnly
                    value={newsletterEmails.join(', ')}
                    rows={5}
                  />
                  <div className="newsletter-subscribers-actions">
                    <Button type="button" variant="secondary" size="sm" onClick={handleCopyNewsletterEmails}>
                      Copy all emails
                    </Button>
                    {newsletterCopySuccess && <span className="newsletter-subscribers-copied">Copied!</span>}
                  </div>
                </>
              )}
            </div>
          </Card>
        )}
      </section>

      {/* ── Admin Users ────────────────────────────────────────────────────── */}
      <section className="settings-section">
        <div className="settings-section-header">
          <div>
            <h2 className="settings-section-title">Admin Users</h2>
            <p className="settings-section-desc">Manage who has access to the CRM.</p>
          </div>
          <Button variant="primary" size="sm" onClick={openAddModal}>
            + Add user
          </Button>
        </div>

        {usersLoading ? (
          <Card>
            <div className="settings-loading">
              <LoadingSpinner size="md" />
            </div>
          </Card>
        ) : usersError ? (
          <Card>
            <ErrorState message={usersError} onRetry={fetchUsers} />
          </Card>
        ) : (
          <Card>
            {users.length === 0 ? (
              <p className="settings-empty">No admin users found.</p>
            ) : (
              <div className="users-table-wrapper">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th className="users-table-actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td className="users-table-name">{user.full_name}</td>
                        <td className="users-table-email">{user.email}</td>
                        <td>
                          <Badge variant={user.is_active ? 'success' : 'default'}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="users-table-date">{formatDate(user.created_at)}</td>
                        <td className="users-table-actions">
                          <button
                            className="users-action-btn"
                            onClick={() => openEditModal(user)}
                            type="button"
                          >
                            Edit
                          </button>
                          <button
                            className="users-action-btn"
                            onClick={() => openPasswordModal(user)}
                            type="button"
                          >
                            Password
                          </button>
                          <button
                            className="users-action-btn users-action-btn--danger"
                            onClick={() => openDeleteModal(user)}
                            type="button"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}
      </section>

      {/* ── GitHub Repos ──────────────────────────────────────────────────────
          Org-level "which repos can BBH-Bot see at all" step, moved here from
          Project Detail -- see project-brain/pages/settings.md "GitHub Repos". */}
      <section className="settings-section">
        <div className="settings-section-header">
          <div>
            <h2 className="settings-section-title">GitHub Repos</h2>
            <p className="settings-section-desc">
              Repos the GitHub App can see. Linking a specific repo to a project happens on that project's detail page.
            </p>
          </div>
          <Button variant="primary" size="sm" onClick={handleGrantGithubAccess} disabled={installUrlLoading}>
            {installUrlLoading ? 'Starting…' : 'Grant access to another repo'}
          </Button>
        </div>

        {githubAppToast && <p className="settings-section-desc">{githubAppToast}</p>}

        {githubReposLoading ? (
          <Card>
            <div className="settings-loading">
              <LoadingSpinner size="md" />
            </div>
          </Card>
        ) : githubReposError ? (
          <Card>
            <ErrorState message={githubReposError} onRetry={fetchGithubRepos} />
          </Card>
        ) : (
          <Card>
            {githubRepos.length === 0 ? (
              <p className="settings-empty">No repos authorized yet.</p>
            ) : (
              <ul className="settings-empty" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {githubRepos.map((r) => (
                  <li key={r.full_name} style={{ padding: 'var(--spacing-xs) 0' }}>
                    <a
                      href={`https://github.com/${r.full_name}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="detail-field-link"
                    >
                      {r.full_name}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}
      </section>

      {/* ── Document Template Modal ─────────────────────────────────────────── */}
      {templateModalMode !== null && selectedTemplate && (
        <div className="modal-overlay" onClick={closeTemplateModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Edit document template</h3>
              <button className="modal-close" onClick={closeTemplateModal} type="button" aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSaveTemplate} noValidate>
              <div className="modal-body">
                <Input
                  label="Name *"
                  value={formTemplateName}
                  onChange={(e) => setFormTemplateName(e.target.value)}
                  placeholder="e.g. Standard Consulting Terms"
                  autoFocus
                />
                <Input
                  label="Description"
                  value={formTemplateDesc}
                  onChange={(e) => setFormTemplateDesc(e.target.value)}
                  placeholder="Brief description of this template"
                />
                {templateModalError && <p className="modal-error">{templateModalError}</p>}
              </div>
              <div className="modal-footer">
                <Button type="submit" variant="primary" disabled={templateSaving}>
                  {templateSaving ? 'Saving…' : 'Save changes'}
                </Button>
                <Button type="button" variant="secondary" onClick={closeTemplateModal} disabled={templateSaving}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Cost Model Modal ────────────────────────────────────────────────── */}
      {costModelModalMode !== null && selectedCostModel && (
        <div className="modal-overlay" onClick={closeCostModelModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Edit cost model</h3>
              <button className="modal-close" onClick={closeCostModelModal} type="button" aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSaveCostModel} noValidate>
              <div className="modal-body">
                <Input
                  label="Label *"
                  value={formCostModelLabel}
                  onChange={(e) => setFormCostModelLabel(e.target.value)}
                  placeholder="e.g. Fixed fee"
                  autoFocus
                />
                <div className="modal-checkbox-row">
                  <label className="modal-checkbox-label">
                    <input
                      type="checkbox"
                      checked={formCostModelActive}
                      onChange={(e) => setFormCostModelActive(e.target.checked)}
                      className="modal-checkbox"
                    />
                    Active (shown in project form)
                  </label>
                </div>
                {costModelModalError && <p className="modal-error">{costModelModalError}</p>}
              </div>
              <div className="modal-footer">
                <Button type="submit" variant="primary" disabled={costModelSaving}>
                  {costModelSaving ? 'Saving…' : 'Save changes'}
                </Button>
                <Button type="button" variant="secondary" onClick={closeCostModelModal} disabled={costModelSaving}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Email Automation Modal ──────────────────────────────────────────── */}
      {emailAutomationModalMode !== null && selectedEmailAutomation && (
        <div className="modal-overlay" onClick={closeEmailAutomationModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Edit mail automation</h3>
              <button
                className="modal-close"
                onClick={closeEmailAutomationModal}
                type="button"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="modal-subtitle">
              Key: <strong>{selectedEmailAutomation.key}</strong>
            </p>
            <form onSubmit={handleSaveEmailAutomation} noValidate>
              <div className="modal-body">
                <Input
                  label="Name *"
                  value={formEmailAutomationName}
                  onChange={(e) => setFormEmailAutomationName(e.target.value)}
                  placeholder="e.g. Website enquiry confirmation"
                  autoFocus
                />
                <Input
                  label="Make.com scenario URL"
                  type="url"
                  value={formEmailAutomationScenarioUrl}
                  onChange={(e) => setFormEmailAutomationScenarioUrl(e.target.value)}
                  placeholder="https://www.make.com/..."
                />
                {emailAutomationModalError && <p className="modal-error">{emailAutomationModalError}</p>}
              </div>
              <div className="modal-footer">
                <Button type="submit" variant="primary" disabled={emailAutomationSaving}>
                  {emailAutomationSaving ? 'Saving…' : 'Save changes'}
                </Button>
                <Button type="button" variant="secondary" onClick={closeEmailAutomationModal} disabled={emailAutomationSaving}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── User Modals ─────────────────────────────────────────────────────── */}

      {userModalMode !== null && (
        <div className="modal-overlay" onClick={closeUserModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>

            {/* Add User */}
            {userModalMode === 'add' && (
              <>
                <div className="modal-header">
                  <h3 className="modal-title">Add admin user</h3>
                  <button className="modal-close" onClick={closeUserModal} type="button" aria-label="Close">×</button>
                </div>
                <form onSubmit={handleAddUser} noValidate>
                  <div className="modal-body">
                    <Input
                      label="Full name *"
                      value={formFullName}
                      onChange={(e) => setFormFullName(e.target.value)}
                      placeholder="e.g. Jane Smith"
                      autoFocus
                    />
                    <Input
                      label="Email *"
                      type="email"
                      value={formEmail}
                      onChange={(e) => setFormEmail(e.target.value)}
                      placeholder="jane@backbyhumans.com"
                    />
                    <Input
                      label="Password * (min 8 characters)"
                      type="password"
                      value={formPassword}
                      onChange={(e) => setFormPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                    {modalError && <p className="modal-error">{modalError}</p>}
                  </div>
                  <div className="modal-footer">
                    <Button type="submit" variant="primary" disabled={saving}>
                      {saving ? 'Creating…' : 'Create user'}
                    </Button>
                    <Button type="button" variant="secondary" onClick={closeUserModal} disabled={saving}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </>
            )}

            {/* Edit User */}
            {userModalMode === 'edit' && selectedUser && (
              <>
                <div className="modal-header">
                  <h3 className="modal-title">Edit user</h3>
                  <button className="modal-close" onClick={closeUserModal} type="button" aria-label="Close">×</button>
                </div>
                <form onSubmit={handleEditUser} noValidate>
                  <div className="modal-body">
                    <Input
                      label="Full name *"
                      value={formFullName}
                      onChange={(e) => setFormFullName(e.target.value)}
                      placeholder="e.g. Jane Smith"
                      autoFocus
                    />
                    <Input
                      label="Email *"
                      type="email"
                      value={formEmail}
                      onChange={(e) => setFormEmail(e.target.value)}
                      placeholder="jane@backbyhumans.com"
                    />
                    <div className="modal-checkbox-row">
                      <label className="modal-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formIsActive}
                          onChange={(e) => setFormIsActive(e.target.checked)}
                          className="modal-checkbox"
                        />
                        Active
                      </label>
                    </div>
                    {modalError && <p className="modal-error">{modalError}</p>}
                  </div>
                  <div className="modal-footer">
                    <Button type="submit" variant="primary" disabled={saving}>
                      {saving ? 'Saving…' : 'Save changes'}
                    </Button>
                    <Button type="button" variant="secondary" onClick={closeUserModal} disabled={saving}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </>
            )}

            {/* Change Password */}
            {userModalMode === 'password' && selectedUser && (
              <>
                <div className="modal-header">
                  <h3 className="modal-title">Change password</h3>
                  <button className="modal-close" onClick={closeUserModal} type="button" aria-label="Close">×</button>
                </div>
                <p className="modal-subtitle">Changing password for <strong>{selectedUser.full_name}</strong></p>
                <form onSubmit={handleChangePassword} noValidate>
                  <div className="modal-body">
                    <Input
                      label="New password * (min 8 characters)"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      autoFocus
                    />
                    <Input
                      label="Confirm new password *"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                    {modalError && <p className="modal-error">{modalError}</p>}
                  </div>
                  <div className="modal-footer">
                    <Button type="submit" variant="primary" disabled={saving}>
                      {saving ? 'Saving…' : 'Change password'}
                    </Button>
                    <Button type="button" variant="secondary" onClick={closeUserModal} disabled={saving}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </>
            )}

            {/* Delete Confirmation */}
            {userModalMode === 'delete' && selectedUser && (
              <>
                <div className="modal-header">
                  <h3 className="modal-title">Delete user</h3>
                  <button className="modal-close" onClick={closeUserModal} type="button" aria-label="Close">×</button>
                </div>
                <div className="modal-body">
                  <p className="modal-confirm-text">
                    Are you sure you want to delete <strong>{selectedUser.full_name}</strong> ({selectedUser.email})?
                    This action cannot be undone.
                  </p>
                  {modalError && <p className="modal-error">{modalError}</p>}
                </div>
                <div className="modal-footer">
                  <Button variant="danger" onClick={handleDeleteUser} disabled={saving}>
                    {saving ? 'Deleting…' : 'Delete user'}
                  </Button>
                  <Button type="button" variant="secondary" onClick={closeUserModal} disabled={saving}>
                    Cancel
                  </Button>
                </div>
              </>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;